import router from '@/router'
import { useAccountStore } from '@/stores/account'
import axios, { AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import type { MessageApiInjection } from 'naive-ui/es/message/src/MessageProvider'
import { clearData, isElectron } from '@/utils/common'
import i18n from '@/i18n'
import { I18nKeys } from '@/constants/i18n'
import { useGlobalStore } from '@/stores/global'
import { EmitKeys, ErrorTaskType, IS_INTL } from '@/constants/global'
import { ErrorCode, ErrorCodeI18nKeysMap } from '@/constants/error'
import { bus } from '@/main'

interface Response {
  code: string
  data: any
  msg: string
}

const goLogin = () => router.push({ name: 'login' })
const client = axios.create({
  baseURL: import.meta.env.DEV ? '/api' : import.meta.env.VITE_BASE_URL,
  timeout: 20000
})

client.interceptors.request.use(
  async (config) => {
    const accountStore = useAccountStore()
    // 内部后端请求
    const isApi =
      (config.url?.startsWith('/') || config.url?.startsWith(import.meta.env.VITE_BASE_URL_V2)) &&
      !config.url?.includes('facebook/link')
    if (isApi) {
      config.headers.Authorization = accountStore.token
    }
    if (IS_INTL) {
      // @ts-ignore
      config.headers.language = i18n.global.locale.value
    }
    // config.headers['Access-Control-Allow-Origin'] = '*'
    if (
      isApi &&
      !config.headers.Authorization &&
      router.currentRoute.value.name !== 'login' &&
      router.currentRoute.value.name !== 'register'
    ) {
      console.info('config', config)
      clearData()
      goLogin()
      return Promise.reject({ response: { status: 401 }, message: 'Session expired' })
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

client.interceptors.response.use(
  (response) => {
    const globalStore = useGlobalStore()
    globalStore.setServerAvailable(true)
    return response
  },
  (error: AxiosError) => {
    const globalStore = useGlobalStore()
    if (error.code === 'ERR_NETWORK') {
      if (globalStore.serverAvailable) {
        globalStore.setServerAvailable(false)
        // 3秒自动关闭提示
        setTimeout(() => {
          globalStore.setServerAvailable(true)
        }, 3000)
      }
      return Promise.reject(error)
    }
    globalStore.setServerAvailable(true)
    const httpErrorHandler = ApiClient.HttpErrorHandler[error.response?.status!]
    if (typeof httpErrorHandler === 'function') {
      httpErrorHandler(error)
    } else if (error?.message) {
      ;((window as any).$message as MessageApiInjection).error(error.message)
    }
    return Promise.reject(error)
  }
)

class ApiClient {
  static HttpErrorHandler: Record<number, (error?: AxiosError) => void> = {
    400: () => {
      // @ts-ignore
      const t = i18n.global.t
      return ((window as any).$message as MessageApiInjection).error(t(I18nKeys.system.error400))
    },
    401: () => {
      clearData()
      goLogin()
    },
    403: () => {
      // @ts-ignore
      const t = i18n.global.t
      return ((window as any).$message as MessageApiInjection).error(t(I18nKeys.system.error403))
    },
    404: () => {
      // @ts-ignore
      const t = i18n.global.t
      return ((window as any).$message as MessageApiInjection).error(t(I18nKeys.system.error404))
    },
    408: () => {
      // @ts-ignore
      const t = i18n.global.t
      return ((window as any).$message as MessageApiInjection).error(t(I18nKeys.system.error408))
    },
    500: (error?: AxiosError) => {
      // @ts-ignore
      const t = i18n.global.t
      return ((window as any).$message as MessageApiInjection).error(
        import.meta.env.DEV && error?.message ? error.message : t(I18nKeys.system.error500)
      )
    },
    501: () => {
      // @ts-ignore
      const t = i18n.global.t
      return ((window as any).$message as MessageApiInjection).error(t(I18nKeys.system.error501))
    },
    502: () => {
      // @ts-ignore
      const t = i18n.global.t
      return ((window as any).$message as MessageApiInjection).error(t(I18nKeys.system.error502))
    },
    503: () => {
      // @ts-ignore
      const t = i18n.global.t
      return ((window as any).$message as MessageApiInjection).error(t(I18nKeys.system.error503))
    },
    504: () => {
      // @ts-ignore
      const t = i18n.global.t
      return ((window as any).$message as MessageApiInjection).error(t(I18nKeys.system.error504))
    }
  }
  static errorHandler: Record<string, (error: Response, showMessage?: boolean) => Promise<any>> = {
    [ErrorCode.INVITATION_CODE_USED]: (resp: Response) => {
      return Promise.reject(resp)
    },
    [ErrorCode.INVITATION_CODE_INVALID]: (resp: Response) => {
      return Promise.reject(resp)
    },
    [ErrorCode.GENERATE_LIMIT]: (resp: Response) => {
      const accountStore = useAccountStore()
      const freeUsage = accountStore.freeUsage
      const memberInfo = accountStore.memberInfo
      const guideTaskExpired = accountStore.guideTaskExpired
      if (
        IS_INTL &&
        !isElectron() &&
        !memberInfo?.userMember &&
        freeUsage.limit - freeUsage.count <= 0 &&
        !guideTaskExpired
      ) {
        // 国际版，非客户端，没有会员，免费额度已用完，且引导任务未过期，提示安装客户端领取一天
        bus.emit(EmitKeys.SHOW_FREE_COUNT_LIMIT_DIALOG)
      } else {
        // @ts-ignore
        bus.emit(EmitKeys.LIMIT, { taskType: ErrorTaskType.IMAGE_GENERATE, data: resp!.data })
      }
      console.info('Reached image generation rate limit', resp)
      return Promise.reject(resp)
    },
    default: (resp: Response, showMessage = true) => {
      if (showMessage) {
        const errorCodeI18nKey = ErrorCodeI18nKeysMap[resp.code]
        if (errorCodeI18nKey) {
          const t = i18n.global.t
          ;((window as any).$message as MessageApiInjection).error(
            t(I18nKeys.error[errorCodeI18nKey])
          )
        } else {
          ;((window as any).$message as MessageApiInjection).error(resp!.msg)
        }
      }
      return Promise.reject(resp!.msg)
    }
  }

  static async get<T>(
    url: string,
    params?: any,
    showMessage = true,
    config?: AxiosRequestConfig<any>
  ): Promise<T> {
    const res = await client.get(ApiClient._paramsHandler(url, params), config)
    return ApiClient._validateResponse<T>(res, showMessage)
  }

  static async post<T>(
    url: string,
    data?: any,
    showMessage = true,
    config?: AxiosRequestConfig<any>
  ): Promise<T> {
    const res = await client.post<Response>(url, data, config)
    return ApiClient._validateResponse<T>(res, showMessage)
  }

  static async put<T>(
    url: string,
    data?: any,
    showMessage = true,
    config?: AxiosRequestConfig<any>
  ): Promise<T> {
    const res = await client.put<Response>(url, data, config)
    return ApiClient._validateResponse<T>(res, showMessage)
  }

  static async delete(
    url: string,
    showMessage = true,
    config?: AxiosRequestConfig<any>
  ): Promise<boolean> {
    const res = await client.delete(url, config)
    return ApiClient._validateResponse(res, showMessage)
  }

  static async head(url: string, showMessage = false, config?: AxiosRequestConfig<any>) {
    const res = await client.head(url, config)
    return ApiClient._validateResponse(res, showMessage)
  }

  private static _paramsHandler(url: string, params?: any): string {
    if (!params) {
      return url
    }
    let handledUrl: string
    if (typeof params === 'string') {
      // 写好的参数字符串
      url.indexOf('?') > -1 ? (handledUrl = `${url}&${params}`) : (handledUrl = `${url}?${params}`)
    } else if (params instanceof Object) {
      // 传入对象
      const paramArr: string[] = []
      Object.keys(params).forEach((key) => {
        const param = params[key]
        if (param === null || param === undefined) return
        paramArr.push(`${key}=${params[key]}`)
      })
      if (paramArr.length > 0) {
        url.indexOf('?') > -1
          ? (handledUrl = `${url}&${paramArr.join('&')}`)
          : (handledUrl = `${url}?${paramArr.join('&')}`)
      } else {
        handledUrl = url
      }
    } else {
      handledUrl = url
    }

    return handledUrl
  }

  private static _validateResponse = <T>(
    res: AxiosResponse<Response>,
    showMessage = true
  ): Promise<T> => {
    // 内部后端请求
    const isApi =
      res.config.url?.startsWith('/') ||
      res.config.url?.startsWith(import.meta.env.VITE_BASE_URL_V2)
    if (!isApi) {
      // 外部请求
      if (res.status === 200) {
        return Promise.resolve(res as T)
      }
      if (showMessage) {
        ;((window as any).$message as MessageApiInjection).error(`Request failed: ${res.status}`)
      }
      return Promise.reject(res)
    }
    const resp = res.data
    if (resp.code === '0000') {
      return resp.data
    } else if (res.config.url && res.config.url.includes('/user/login') && resp.code === '10022') {
      return Promise.reject(resp)
    } else {
      const errorHandler = ApiClient.errorHandler[resp.code] || ApiClient.errorHandler.default
      return errorHandler(resp, showMessage)
    }
  }
}

export default ApiClient
